import * as THREE from 'three';
import type { Network, Station } from '../data/types';
import type { ElevationIndex } from '../geometry/elevation';
import { levelToHeight } from '../geometry/levels';

export class StationsLayer {
  readonly group = new THREE.Group();
  readonly labels = new THREE.Group();
  private levelsGroup = new THREE.Group();
  private levelItems: { pad: THREE.Mesh; label: THREE.Sprite; base: number }[] = [];
  private markers: { mesh: THREE.Mesh; station: Station }[] = [];
  private pickDiscs: THREE.Mesh[] = [];
  private markerMat: THREE.MeshBasicMaterial;
  private selectedId: string | null = null;

  constructor() {
    this.group.name = 'stations';
    this.labels.name = 'station-labels';
    this.levelsGroup.name = 'station-levels';
    this.markerMat = new THREE.MeshBasicMaterial({ color: 0xdfe8f2 });
  }

  build(network: Network, elev: ElevationIndex) {
    this.group.add(this.labels);
    this.group.add(this.levelsGroup);
    // platform polygons grouped by their vertical level
    const buckets = new Map<string, THREE.BufferGeometry[]>();
    for (const p of network.platforms) {
      if (p.polygon.length < 3) continue;
      const lv = p.level == null || p.level === '' ? null : Number(p.level);
      const y = lv == null ? elev.heightAt(p.polygon[0][0], p.polygon[0][1]) : levelToHeight(lv);
      const shape = new THREE.Shape(p.polygon.map(([x, z]) => new THREE.Vector2(x, -z)));
      const g = new THREE.ShapeGeometry(shape);
      g.rotateX(-Math.PI / 2);
      const key = String(Math.round(y));
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(g);
    }
    const mat = new THREE.MeshBasicMaterial({
      color: 0x7fa8c9,
      transparent: true,
      opacity: 0.22,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    for (const [key, geos] of buckets) {
      const merged = mergeGeometries(geos);
      const mesh = new THREE.Mesh(merged, mat);
      mesh.position.y = Number(key) + 0.5;
      mesh.renderOrder = 1;
      this.group.add(mesh);
    }

    // station markers + labels
    const ringGeo = new THREE.RingGeometry(6, 9, 20);
    // larger, invisible pick surface for reliable hover/click at every zoom
    const pickGeo = new THREE.CircleGeometry(1, 20);
    const pickMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
    for (const s of network.stations) {
      if (s.name === 'Unbenannt') continue;
      const mesh = new THREE.Mesh(ringGeo, this.markerMat.clone());
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(s.x, 1, s.z);
      mesh.userData.station = s;
      this.markers.push({ mesh, station: s });
      this.group.add(mesh);

      const disc = new THREE.Mesh(pickGeo, pickMat);
      disc.rotation.x = -Math.PI / 2;
      disc.scale.setScalar(24);
      disc.position.set(s.x, 1, s.z);
      disc.userData.station = s;
      this.pickDiscs.push(disc);
      this.group.add(disc);

      this.labels.add(this.makeLabel(s));
    }

    this.buildLevels(network);
  }

  /** Schematic multi-level station representation: platforms, level pads and labels. */
  private buildLevels(network: Network) {
    const padGeo = new THREE.CircleGeometry(1, 18);
    for (const s of network.stations) {
      if (!s.multiLevel || !s.levels || s.levels.length < 2) continue;
      for (const lvl of s.levels) {
        const base = levelToHeight(lvl.level);
        const underground = (lvl.level ?? 0) < 0;
        const color = underground ? 0x8ab4f8 : 0xf2c14e;
        const mat = new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.7,
          side: THREE.DoubleSide,
          depthWrite: false,
        });
        const pad = new THREE.Mesh(padGeo, mat);
        pad.rotation.x = -Math.PI / 2;
        const radius = 16 + lvl.platformCount * 5;
        pad.scale.setScalar(radius);
        pad.position.set(lvl.x, base + (underground ? 1.5 : 1.2), lvl.z);
        pad.renderOrder = 3;
        this.levelsGroup.add(pad);
        const label = this.makeLevelLabel(`${s.name} — ${lvl.label}`);
        label.position.set(lvl.x, base + 26, lvl.z);
        this.levelsGroup.add(label);
        this.levelItems.push({ pad, label, base });
      }
    }
  }

  private makeLevelLabel(text: string): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 56;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = '500 24px system-ui, sans-serif';
    ctx.fillStyle = 'rgba(180,205,235,0.95)';
    ctx.textBaseline = 'middle';
    ctx.fillText(text.slice(0, 42), 6, 30);
    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false });
    const sprite = new THREE.Sprite(mat);
    const scale = 70;
    sprite.scale.set(scale, scale / 9, 1);
    sprite.renderOrder = 6;
    return sprite;
  }

  setLevelsVisible(v: boolean) {
    this.levelsGroup.visible = v;
  }

  /** View-only transform: separate levels further apart for readability. */
  setExplode(on: boolean) {
    const f = on ? 2.5 : 1;
    for (const item of this.levelItems) {
      item.pad.position.y = item.base * f + (item.base < 0 ? 1.5 : 1.2);
      item.label.position.y = item.base * f + 26;
    }
  }

  private makeLabel(s: Station): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 512, 64);
    ctx.font = '600 30px system-ui, sans-serif';
    ctx.fillStyle = 'rgba(230,237,243,0.92)';
    ctx.textBaseline = 'middle';
    ctx.fillText(s.name, 6, 34);
    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false });
    const sprite = new THREE.Sprite(mat);
    const scale = 90;
    sprite.scale.set(scale, scale / 8, 1);
    sprite.position.set(s.x, 18, s.z);
    sprite.userData.stationId = s.id;
    sprite.renderOrder = 5;
    return sprite;
  }

  get pickTargets(): THREE.Object3D[] {
    return [...this.markers.map((m) => m.mesh), ...this.pickDiscs];
  }

  stationFor(object: THREE.Object3D): Station | null {
    const st = (object.userData as { station?: Station }).station;
    return st ?? null;
  }

  select(id: string | null) {
    this.selectedId = id;
    for (const { mesh, station } of this.markers) {
      const mat = mesh.material as THREE.MeshBasicMaterial;
      if (station.id === id) {
        mat.color.setHex(0xffd166);
        mesh.scale.setScalar(1.6);
      } else {
        mat.color.setHex(0xdfe8f2);
        mesh.scale.setScalar(1);
      }
    }
  }

  /** zoom-dependent label visibility: hide labels when zoomed far out */
  updateLabels(cameraPos: THREE.Vector3, selectedId: string | null, selectionActive: boolean) {
    for (const child of this.labels.children) {
      const sprite = child as THREE.Sprite;
      const id = sprite.userData.stationId as string;
      const dist = Math.hypot(
        cameraPos.x - sprite.position.x,
        cameraPos.y - sprite.position.y,
        cameraPos.z - sprite.position.z,
      );
      const show = dist < 9000 || id === selectedId || selectionActive;
      sprite.visible = show;
    }
  }
}

function mergeGeometries(geos: THREE.BufferGeometry[]): THREE.BufferGeometry {
  let vertexCount = 0;
  let indexCount = 0;
  for (const g of geos) {
    vertexCount += g.attributes.position.count;
    indexCount += g.index ? g.index.count : g.attributes.position.count;
  }
  const pos = new Float32Array(vertexCount * 3);
  const idx = new Uint32Array(indexCount);
  let vOff = 0;
  let iOff = 0;
  for (const g of geos) {
    const p = g.attributes.position;
    pos.set(p.array as Float32Array, vOff * 3);
    if (g.index) {
      for (let i = 0; i < g.index.count; i++) idx[iOff++] = g.index.getX(i) + vOff;
    } else {
      for (let i = 0; i < p.count; i++) idx[iOff++] = i + vOff;
    }
    vOff += p.count;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setIndex(new THREE.BufferAttribute(idx, 1));
  out.computeVertexNormals();
  return out;
}
